import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { startOfWeek, endOfWeek } from 'date-fns';
import { temporadaAPI } from '../../services/api';

const STORAGE_KEY = 'patagoniaVital:season';

const isBrowser = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const readFromStorage = () => {
  if (!isBrowser) return null;

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error leyendo temporada desde localStorage', error);
    return null;
  }
};

const writeToStorage = (data) => {
  if (!isBrowser) return;

  try {
    if (data) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    console.error('Error guardando temporada en localStorage', error);
  }
};

export const getWeekNumber = (date) => {
  const target = new Date(date.valueOf());
  const dayNr = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) {
    target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
  }
  const dateDiff = firstThursday - target.valueOf();
  return 1 + Math.round(dateDiff / (7 * 24 * 3600 * 1000));
};

export const getDateOfISOWeek = (week, year) => {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dayOfWeek = simple.getDay();
  const ISOweekStart = new Date(simple);

  if (dayOfWeek <= 4 && dayOfWeek !== 0) {
    ISOweekStart.setDate(simple.getDate() - (dayOfWeek - 1));
  } else {
    ISOweekStart.setDate(simple.getDate() + (8 - dayOfWeek));
  }

  return ISOweekStart;
};

export const getWeekRange = (year, week) => {
  const weekDate = getDateOfISOWeek(week, year);
  const start = startOfWeek(weekDate, { weekStartsOn: 1 });
  const end = endOfWeek(weekDate, { weekStartsOn: 1 });

  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
};

const clampWeekToSeason = (week, season) => {
  if (!season) return week;
  if (week < season.semana_inicio) return season.semana_inicio;
  if (week > season.semana_fin) return season.semana_fin;
  return week;
};

const isValidSelection = (seasons, year, week) => {
  if (!year || !week) return false;
  const season = seasons.find((item) => item.anio === year);
  if (!season) return false;
  return week >= season.semana_inicio && week <= season.semana_fin;
};

const buildSelection = (seasons, year, week) => {
  const season = seasons.find((item) => item.anio === year);
  if (!season) return null;

  const normalizedWeek = clampWeekToSeason(week, season);
  return {
    year: season.anio,
    week: normalizedWeek,
    range: getWeekRange(season.anio, normalizedWeek),
    season,
  };
};

const resolveDefaultSelection = (seasons) => {
  if (!seasons || seasons.length === 0) {
    return null;
  }

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentWeek = getWeekNumber(now);

  const sorted = [...seasons].sort((a, b) => a.anio - b.anio);
  let season = sorted.find((item) => item.anio === currentYear);

  if (!season) {
    // Buscar la temporada más cercana al año actual
    const futureSeason = sorted.find((item) => item.anio > currentYear);
    season = futureSeason || sorted[sorted.length - 1];
  }

  const week = clampWeekToSeason(currentWeek, season);
  return buildSelection(seasons, season.anio, week);
};

export const fetchSeasons = createAsyncThunk(
  'season/fetchSeasons',
  async (_, { rejectWithValue }) => {
    try {
      const response = await temporadaAPI.getAll();
      return response.data || [];
    } catch (error) {
      return rejectWithValue(error.message || 'Error al obtener temporadas');
    }
  }
);

const initialSelected = readFromStorage();

const initialState = {
  availableSeasons: [],
  selected: initialSelected,
  loading: false,
  error: null,
};

const seasonSlice = createSlice({
  name: 'season',
  initialState,
  reducers: {
    setSeason: (state, action) => {
      const { year, week } = action.payload;
      const selection = buildSelection(state.availableSeasons, year, week);

      if (selection) {
        state.selected = selection;
        writeToStorage({ year: selection.year, week: selection.week, range: selection.range });
      }
    },
    resetSeason: (state) => {
      const selection = resolveDefaultSelection(state.availableSeasons);

      if (selection) {
        state.selected = selection;
        writeToStorage({ year: selection.year, week: selection.week, range: selection.range });
      } else {
        state.selected = null;
        writeToStorage(null);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSeasons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSeasons.fulfilled, (state, action) => {
        state.loading = false;
        const seasons = action.payload.sort((a, b) => a.anio - b.anio);
        state.availableSeasons = seasons;

        const stored = state.selected;
        let nextSelection = null;

        if (stored && isValidSelection(seasons, stored.year, stored.week)) {
          nextSelection = buildSelection(seasons, stored.year, stored.week);
        } else {
          nextSelection = resolveDefaultSelection(seasons);
        }

        state.selected = nextSelection;
        if (nextSelection) {
          writeToStorage({ year: nextSelection.year, week: nextSelection.week, range: nextSelection.range });
        } else {
          writeToStorage(null);
        }
      })
      .addCase(fetchSeasons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'No fue posible cargar las temporadas';
      });
  },
});

export const { setSeason, resetSeason } = seasonSlice.actions;

export const selectSeason = (state) => state.season.selected;
export const selectAvailableSeasons = (state) => state.season.availableSeasons;
export const selectSeasonLoading = (state) => state.season.loading;
export const selectSeasonError = (state) => state.season.error;

export default seasonSlice.reducer;
